import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  message,
  DatePicker,
  Spin,
} from "antd";
import moment from "moment";
import api from "../services/api";

const { Option } = Select;
const { RangePicker } = DatePicker;

const Contests = () => {
  const [contests, setContests] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContest, setEditingContest] = useState(null);

  // Fetch contests
  const fetchContests = async () => {
    setLoading(true);
    try {
      const res = await api.get("/contests");
      setContests(Array.isArray(res.data) ? res.data : res.data.data || []);
    } catch (error) {
      message.error("Failed to fetch contests");
    } finally {
      setLoading(false);
    }
  };

  // Fetch tournaments
  const fetchTournaments = async () => {
    try {
      const res = await api.get("/tournaments");
      setTournaments(Array.isArray(res.data) ? res.data : res.data.data || []);
    } catch (error) {
      message.error("Failed to load tournaments");
    }
  };

  useEffect(() => {
    fetchContests();
    fetchTournaments();
  }, []);

  // Add/Edit submit
  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        start_time: values.timeRange[0].toISOString(),
        end_time: values.timeRange[1].toISOString(),
      };
      delete payload.timeRange;

      if (editingContest) {
        await api.put(`/contests/${editingContest.contest_id}`, payload);
        message.success("Contest updated successfully");
      } else {
        await api.post("/contests", payload);
        message.success("Contest added successfully");
      }

      fetchContests();
      setIsModalVisible(false);
      form.resetFields();
      setEditingContest(null);
    } catch (error) {
      message.error("Operation failed");
    }
  };

  // Delete
  const handleDelete = async (id) => {
    try {
      await api.delete(`/contests/${id}`);
      message.success("Contest deleted");
      fetchContests();
    } catch (error) {
      message.error("Failed to delete");
    }
  };

  // Columns (mobile-friendly)
  const columns = [
    {
      title: "ID",
      dataIndex: "contest_id",
      width: 80,
      fixed: "left",
    },
    {
      title: "Tournament",
      dataIndex: "tournament_name",
      ellipsis: true,
      width: 160,
    },
    {
      title: "Name",
      dataIndex: "name",
      ellipsis: true,
      width: 160,
    },
    {
      title: "Entry Fee",
      dataIndex: "entry_fee",
      width: 120,
    },
    {
      title: "Prize Pool",
      dataIndex: "prize_pool",
      width: 120,
    },
    {
      title: "Max",
      dataIndex: "max_participants",
      width: 100,
    },
    {
      title: "Joined",
      dataIndex: "joined_participants",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (t) => t.charAt(0).toUpperCase() + t.slice(1),
    },
    {
      title: "Time",
      width: 240,
      render: (_, r) =>
        r.start_time && r.end_time
          ? `${moment(r.start_time).format("YYYY-MM-DD HH:mm")} - ${moment(
              r.end_time
            ).format("YYYY-MM-DD HH:mm")}`
          : "-",
    },
    {
      title: "Actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Button
            size="small"
            type="link"
            onClick={() => {
              setEditingContest(record);
              form.setFieldsValue({
                ...record,
                tournament_id: record.tournament_id,
                timeRange: [
                  moment(record.start_time),
                  moment(record.end_time),
                ],
              });
              setIsModalVisible(true);
            }}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete contest?"
            onConfirm={() => handleDelete(record.contest_id)}
          >
            <Button size="small" type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2>Contests Management</h2>

        <Button
          type="primary"
          onClick={() => {
            setEditingContest(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
        >
          + Add Contest
        </Button>
      </div>

      {loading ? (
        <Spin tip="Loading contests..." />
      ) : (
        <Table
          columns={columns}
          dataSource={contests}
          rowKey="contest_id"
          bordered
          scroll={{ x: 1300 }} // ★ mobile horizontal scroll
          size="small"
        />
      )}

      {/* MODAL */}
      <Modal
        title={editingContest ? "Edit Contest" : "Add Contest"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        okText={editingContest ? "Update" : "Add"}
        className="mobile-modal"
        style={{ top: 20 }}
        bodyStyle={{ padding: 16 }}
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item
            label="Tournament"
            name="tournament_id"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select Tournament">
              {tournaments.map((t) => (
                <Option key={t.tournament_id} value={t.tournament_id}>
                  {t.name || `Tournament ${t.tournament_id}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Contest Name"
            name="name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Entry Fee"
            name="entry_fee"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Prize Pool"
            name="prize_pool"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Max Participants"
            name="max_participants"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="upcoming">Upcoming</Option>
              <Option value="live">Live</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Start & End Time"
            name="timeRange"
            rules={[{ required: true }]}
          >
            <RangePicker showTime style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Rules (JSON)" name="rules">
            <Input.TextArea rows={3} placeholder='{"max_wickets":10}' />
          </Form.Item>
        </Form>
      </Modal>

      {/* Responsive Modal CSS */}
      <style>
        {`
          @media (max-width: 600px) {
            .mobile-modal .ant-modal {
              width: 95% !important;
              max-width: none !important;
              margin: 0 auto;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Contests;
